import React from 'react';

import { EntityType } from 'controls/designer/features/ceeview-entity-drop/types';
import { openModal } from 'controls/react/ant/antModal';
import { DataSourceDataConstructor } from 'areas/data-sources/components/DataSourceDataConstructor';
import { DataSourceDataConstructorResult } from 'areas/data-sources/components/DataSourceDataConstructor/types';
import { getWidgetDescription } from 'controls/designer/features/widgets/utils/getWidgetDescription';
import { PresentationType } from 'controls/designer/features/ceeview-entity-drop/presentationType';
import { DataSourceEntityDescription } from 'areas/data-sources/types/dataSource';
import { DataRegistryEntityDescription } from 'areas/management/data-registry/dataRegistryEntry';
import { DataSourcesGridWidgetConfig } from 'areas/data-sources/components/DataSourceDataGridWidget/types';
import { EntityDescription, ServiceEntityDescription, SlaEntityDescription } from 'framework/EntityDescription';
import { DataSourcesChartWidgetConfig } from 'controls/designer/features/widgets/datasources/datasourceStores';
import { TileDataSourceConfig } from 'controls/designer/features/widgets/datasources/tile/types';
import { CompassWidgetConfig } from 'areas/metrics/widgets/Compass/config';
import { ApplicationState } from 'framework/applicationState';
import { CommonGaugeConfig } from 'areas/metrics/widgets/CommonGauge/types';

export type PresentationMapping<DragEntity extends EntityDescription> = {
	addEntity?: (config: any, entities: DragEntity[]) => void;
	customConfigBuilder?: (data: EntityDescription[]) => any;
	entityType: EntityType;
	imageUrl: string;
	openForm?: boolean;
	presentationType: PresentationType;
	setEntity?: (config: unknown, entity: DragEntity[]) => void;
	widgetType: string;
	reset?: (config: any) => void;
	createEntityDescription: (config: any) => EntityDescription[];
};

let presentationMappings: PresentationMapping<any>[];
export function getPresentationMappings() {
	if (presentationMappings == null) {
		presentationMappings = [
			{
				presentationType: PresentationType.Grid,
				entityType: EntityType.Service,
				imageUrl: 'assets-static/dashboard-designer/previews/service-events.png',
				widgetType: 'service-events',
				setEntity: (config: any, entities: ServiceEntityDescription[]) => {
					config.serviceIds = [entities.map((x) => x.id)];
				},
				addEntity: (config: any, entities: ServiceEntityDescription[]) => {
					entities.forEach((x) => config.serviceIds.push(x.id));
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.Preview,
				entityType: EntityType.Service,
				imageUrl: 'assets-static/dashboard-designer/previews/service-preview.png',
				widgetType: 'service-preview',
				setEntity: (config: any, entity: ServiceEntityDescription[]) => {
					config.configuration.serviceId = entity[0].id;
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-multi-graph-asset.png',
				widgetType: 'metric-multi-graph-asset',
				addEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
					entities.forEach((x) => config.configuration.metrics.push(x.id));
					entities.forEach((x) => config.configuration.metricsItems.push({ metricId: x.id }));
				},
				setEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
					config.configuration.metrics = entities.map((x) => x.id);
					config.configuration.metricsItems = entities.map((x) => ({ metricId: x.id }));
					config.configuration.accountId = entities[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return config.configuration.metrics.map((x: string) => ({
						type: EntityType.Metric,
						id: x,
						accountId: config.configuration.accountId,
					}));
				},
			},
			//we have to also update highchart config for the widget, not just widget configuration
			// ,{
			// 	presentationType: PresentationType.Chart,
			// 	entityType: EntityType.Metric,
			// 	imageUrl: "assets-static/dashboard-designer/previews/metric-custom.png",
			// 	widgetType: 'metric-custom',
			// 	addEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
			// 		entities.forEach(x => config.configuration.metricsItems.push(({metricId: x.id})));
			// 		},
			// 	setEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
			// 		config.configuration.metricsItems = entities.map(x => ({
			// 			metricId: x.id
			// 		}));
			// 		config.configuration.accountId = entities[0].accountId;
			// 	},
			// 	createEntityDescription: (config: any) => {
			// 		return config.configuration.metricsItems.map((x: {metricId: string}) => ({
			// 			type: EntityType.Metric,
			// 			id: x.metricId,
			// 			accountId: config.configuration.accountId
			// 		}));
			// 	}
			// },
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-gauge.png',
				widgetType: 'metric-gauge',
				setEntity: (config: any, entity: DataRegistryEntityDescription[]) => {
					config.configuration.metricId = entity[0].id;
					config.configuration.accountId = entity[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return [
						{
							type: EntityType.Metric,
							id: config.configuration.metricId,
							accountId: config.configuration.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-single-graph.png',
				widgetType: 'metric-single-graph',
				setEntity: (config: any, entity: DataRegistryEntityDescription[]) => {
					config.configuration.metrics = [entity[0].id];
					config.configuration.accountId = entity[0].accountId;
					delete config.configuration.metricsItems;
				},
				createEntityDescription: (config: any) => {
					return [
						{
							type: EntityType.Metric,
							id: config.configuration.metrics[0],
							accountId: config.configuration.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-sparkline-table.png',
				widgetType: 'metric-sparkline-table',
				addEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
					entities.forEach((x) =>
						config.selectedMetrics.push({
							metricId: x.id,
						}),
					);
				},
				setEntity: (config: any, entity: DataRegistryEntityDescription[]) => {
					config.selectedMetrics = entity.map((x) => ({
						metricId: x.id,
					}));
					config.accountId = entity[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return config.selectedMetrics.map((x: { metricId: string }) => ({
						type: EntityType.Metric,
						id: x.metricId,
						accountId: config.accountId,
					}));
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-stacked-chart.png',
				widgetType: 'metric-stacked-chart',
				addEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
					entities.forEach((x) => config.configuration.metrics.push(x.id));
					entities.forEach((x) => config.configuration.metricsItems.push({ metricId: x.id }));
				},
				setEntity: (config: any, entities: DataRegistryEntityDescription[]) => {
					config.configuration.metrics = entities.map((x) => x.id);
					config.configuration.metricsItems = entities.map((x) => ({ metricId: x.id }));
					config.configuration.accountId = entities[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return config.configuration.metrics.map((x: string) => ({
						type: EntityType.Metric,
						id: x,
						accountId: config.configuration.accountId,
					}));
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-compass.png',
				widgetType: 'metric-compass',
				setEntity: (config: CompassWidgetConfig, entity: DataRegistryEntityDescription[]) => {
					config.metric.metricId = entity[0].id;
				},
				createEntityDescription: (config: CompassWidgetConfig) => {
					return [
						{
							type: EntityType.Metric,
							id: config.metric.metricId,
							accountId: ApplicationState.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-compass.png',
				widgetType: 'metric-common-gauge',
				setEntity: (config: CommonGaugeConfig, entity: DataRegistryEntityDescription[]) => {
					config.metric.metricId = entity[0].id;
				},
				createEntityDescription: (config: CommonGaugeConfig) => {
					return [
						{
							type: EntityType.Metric,
							id: config.metric.metricId,
							accountId: ApplicationState.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-barchart.png',
				widgetType: 'metric-barchart',
				setEntity: (config: any, entity: DataRegistryEntityDescription[]) => {
					config.selectedMetrics = [
						{
							metricId: entity[0].id,
						},
					];
					config.accountId = entity[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return [
						{
							type: EntityType.Metric,
							id: config.selectedMetrics[0].metricId,
							accountId: config.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Grid,
				entityType: EntityType.Metric,
				imageUrl: 'assets-static/dashboard-designer/previews/metric-table.png',
				widgetType: 'metric-table',
				setEntity: (config: any, entity: DataRegistryEntityDescription[]) => {
					config.metricId = entity[0].id;
					config.accountId = entity[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return [
						{
							type: EntityType.Metric,
							id: config.metricId,
							accountId: config.accountId,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Custom,
				entityType: EntityType.Sla,
				imageUrl: 'assets-static/dashboard-designer/previews/sla-time.png',
				widgetType: 'sla-time',
				setEntity: (config: any, entity: SlaEntityDescription[]) => {
					config.configuration.slaId = entity[0].id;
					config.configuration.serviceId = entity[0].serviceId;
					config.configuration.accountId = entity[0].accountId;
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.History,
				entityType: EntityType.Sla,
				imageUrl: 'assets-static/dashboard-designer/previews/sla-history.png',
				widgetType: 'sla-history',
				setEntity: (config: any, entity: SlaEntityDescription[]) => {
					config.configuration.slaId = entity[0].id;
					config.configuration.serviceId = entity[0].serviceId;
					config.configuration.accountId = entity[0].accountId;
					config.configuration.periods = 4;
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.History,
				entityType: EntityType.Service,
				imageUrl: 'assets-static/dashboard-designer/previews/service-history.png',
				widgetType: 'service-history',
				openForm: true,
				setEntity: (config: any, entity: ServiceEntityDescription[]) => {
					config.configuration.accountId = entity[0].accountId;
					config.configuration.serviceId = entity[0].id;
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.Sla,
				imageUrl: 'assets-static/dashboard-designer/previews/sla.png',
				widgetType: 'sla',
				setEntity: (config: any, entity: SlaEntityDescription[]) => {
					config.configuration.slaId = entity[0].id;
					config.configuration.serviceId = entity[0].serviceId;
					config.configuration.accountId = entity[0].accountId;
					config.configuration.periods = 4;
				},
				createEntityDescription: (config: any) => {
					return null;
				},
			},
			{
				presentationType: PresentationType.Grid,
				entityType: EntityType.DataSource,
				imageUrl: 'assets-static/dashboard-designer/previews/data-sources-grid.png',
				widgetType: 'data-sources-grid',
				setEntity: (config: DataSourcesGridWidgetConfig, entity: DataSourceEntityDescription[]) => {
					config.dataSourceId = entity[0].id;
					config.dataSourceSelectorAccountId = entity[0].accountId;
					config.accountId = ApplicationState.accountId;
				},
				// reset: (config: DataSourcesGridWidgetConfig) => {
				// 	config.pivotRows = [];
				// 	config.pivotColumns = [];
				// 	config.pivotValues = [];
				// 	config.dataRows = null;
				// 	config.dataColumns = [];
				// 	config.categoryColumns = [];
				// },
				createEntityDescription: (config: DataSourcesGridWidgetConfig) => {
					return [
						{
							accountId: config.dataSourceSelectorAccountId,
							id: config.dataSourceId,
							type: EntityType.DataSource,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.DataSource,
				imageUrl: 'assets-static/dashboard-designer/previews/data-sources-tile.png',
				widgetType: 'data-sources-tile',
				setEntity: (config: TileDataSourceConfig, entity: DataSourceEntityDescription[]) => {
					config.dataSourceSelection.dataSourceId = entity[0].id;
					config.dataSourceSelection.dataSourceSelectorAccountId = entity[0].accountId;
					config.dataSourceSelection.accountId = ApplicationState.accountId;
				},
				createEntityDescription: (config: TileDataSourceConfig) => {
					return [
						{
							accountId: config.dataSourceSelection.dataSourceSelectorAccountId,
							id: config.dataSourceSelection.dataSourceId,
							type: EntityType.DataSource,
						},
					];
				},
			},
			{
				presentationType: PresentationType.Chart,
				entityType: EntityType.DataSource,
				imageUrl: 'assets-static/dashboard-designer/previews/data-sources-chart.png',
				widgetType: 'data-sources-chart',
				setEntity: (config: DataSourcesChartWidgetConfig, entity: DataSourceEntityDescription[]) => {
					config.dataSourceId = entity[0].id;
					config.dataSourceSelectorAccountId = entity[0].accountId;
					config.accountId = ApplicationState.accountId;
				},
				customConfigBuilder: async (entity: EntityDescription[]) => {
					return new Promise<any>((resolve) => {
						let modalData: ReturnType<typeof openModal> = null;

						function onCompleted(result: DataSourceDataConstructorResult) {
							modalData.close();

							const description = getWidgetDescription('data-sources-chart');
							const config = JSON.parse(JSON.stringify(description.defaultConfig));
							config.dataSourceSelectorAccountId = result.accountId;
							config.accountId = ApplicationState.accountId;
							config.dataSourceId = result.dataSourceId;
							config.legendColumn = result.categoryColumn;
							config.columns = result.dataColumns;
							config.rows = result.rows;

							resolve(config);
						}

						modalData = openModal(
							{
								title: null,
								width: 1000,
								height: 400,
								footer: null,
							},
							<DataSourceDataConstructor
								dataSourceId={entity[0].id}
								accountId={entity[0].accountId}
								onCompleted={onCompleted}
							/>,
						);
					});
				},
				createEntityDescription: (config: DataSourcesGridWidgetConfig) => {
					return [
						{
							accountId: config.dataSourceSelectorAccountId,
							id: config.dataSourceId,
							type: EntityType.DataSource,
						},
					];
				},
			},
		];
	}

	return presentationMappings;
}
